import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Heartbeat, CheckCircle } from '@phosphor-icons/react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS = { citada: 'Citada', confirmada: 'Confirmada' };

const fmtDay = (d, lang) => new Date(`${d}T00:00:00`).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

function CallRow({ c, lang, onDone, busy }) {
  return (
    <li data-testid={`phone-call-row-${c.appointment_id}`} className="flex items-center gap-3 py-3 border-b border-stone-100 last:border-0">
      <span className="font-mono text-sm text-stone-800 w-12 shrink-0">{c.time || '--:--'}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-stone-800 truncate">{c.volunteer_id} — {c.volunteer_name}</p>
        {c.phone ? (
          <a data-testid={`phone-call-tel-${c.appointment_id}`} href={`tel:${c.phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline font-mono">
            <Phone size={13} weight="bold" />{c.phone}
          </a>
        ) : <span className="text-xs text-red-600">{lang === 'es' ? 'Sin teléfono' : 'No phone'}</span>}
      </div>
      <Badge variant="outline" className="text-[10px] shrink-0">{STATUS[c.status] || c.status}</Badge>
      <Button data-testid={`phone-call-qsalud-${c.appointment_id}`} size="sm" variant="outline" className="h-8 rounded-sm gap-1 text-emerald-700 border-emerald-300"
        onClick={() => window.open(`/volunteers/${c.volunteer_id}?tab=health`, '_blank')}>
        <Heartbeat size={14} />Qsalud
      </Button>
      <Button data-testid={`phone-call-done-${c.appointment_id}`} size="sm" disabled={busy} className="h-8 rounded-sm gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onDone(c)}>
        <CheckCircle size={14} />{lang === 'es' ? 'Terminada' : 'Done'}
      </Button>
    </li>
  );
}

export function PhoneCallsSheet({ open, onOpenChange, date, lang, onChanged }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/agenda/phone-calls`, { params: { date }, withCredentials: true })
      .then(r => setCalls(r.data || []))
      .catch(() => setCalls([]))
      .finally(() => setLoading(false));
  }, [date]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const markDone = async (c) => {
    setBusyId(c.appointment_id);
    try {
      await axios.put(`${API_URL}/api/appointments/${c.appointment_id}`, { status: 'terminada' }, { withCredentials: true });
      setCalls(prev => prev.filter(x => x.appointment_id !== c.appointment_id));
      toast.success(lang === 'es' ? 'Llamada marcada como terminada' : 'Call marked as done');
      onChanged && onChanged();
    } catch {
      toast.error(lang === 'es' ? 'No se pudo actualizar la cita' : 'Could not update appointment');
    } finally { setBusyId(null); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="phone-calls-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Phone size={18} className="text-emerald-600" />{lang === 'es' ? 'Llamadas del día' : 'Calls of the day'}</SheetTitle>
          <SheetDescription><span className="capitalize">{fmtDay(date, lang)}</span> · QSALUD TFNO · {calls.length} {lang === 'es' ? 'pendientes' : 'pending'}</SheetDescription>
        </SheetHeader>
        {loading ? <p className="text-sm text-stone-500 mt-6">{lang === 'es' ? 'Cargando...' : 'Loading...'}</p>
          : calls.length === 0 ? <p data-testid="phone-calls-empty" className="text-sm text-stone-500 mt-6">{lang === 'es' ? 'No hay llamadas pendientes este día.' : 'No pending calls this day.'}</p>
          : <ul className="mt-4" data-testid="phone-calls-list">{calls.map(c => <CallRow key={c.appointment_id} c={c} lang={lang} onDone={markDone} busy={busyId === c.appointment_id} />)}</ul>}
      </SheetContent>
    </Sheet>
  );
}
